import { InfoCircleOutlined } from '@ant-design/icons';
import EmbedCard from 'components/Embeds/EmbedCard';
import EmbedCardTitleDivier from 'components/Embeds/EmbedCardTitleDivier';
import useIsMobile from 'hooks/useIsMobile';
import useLang from 'hooks/useLang';
import { formatDate, formatTime } from 'utils/generateEntityColumns';
import { getImage } from 'utils/getImage';

const GameEmbedInfo = ({ data, lang, noBorder }) => {
  const isMobile = useIsMobile();
  const LANG = useLang(lang);

  // Helper functions
  const getHomeTeamResult = (game) => game.HomeTeamGoals > game.AwayTeamGoals ? 'win' : game.HomeTeamGoals < game.AwayTeamGoals ? 'loss' : (game.status != 'NOT PLAYED' && game.status != null) ? 'tie' : null;
  const getAwayTeamResult = (game) => game.HomeTeamGoals < game.AwayTeamGoals ? 'win' : game.HomeTeamGoals > game.AwayTeamGoals ? 'loss' : (game.status != 'NOT PLAYED' && game.status != null) ? 'tie' : null;

  // Calculate team stats
  const homeTeamShots =          data.overwriteEnabled && data.AwayTeamSaves == 0 ? 0 : data.HomeTeamGoals + data.AwayTeamSaves /* AWAY = opposite!!! */;
  const awayTeamShots =          data.overwriteEnabled && data.HomeTeamSaves == 0 ? 0 : data.AwayTeamGoals + data.HomeTeamSaves /* HOME = opposite!!! */;
  const homeTeamShots_Original = data.overwriteEnabled ? data.HomeTeamGoals_Original + data.AwayTeamSaves_Original : null; /* AWAY = opposite!!! */;
  const awayTeamShots_Original = data.overwriteEnabled ? data.AwayTeamGoals_Original + data.HomeTeamSaves_Original : null; /* HOME = opposite!!! */;

  const homeTeamFaceOffs =          data.HomeTeamFaceOffs;
  const awayTeamFaceOffs =          data.AwayTeamFaceOffs;
  const homeTeamFaceOffs_Original = data.overwriteEnabled ? data.HomeTeamFaceOffs_Original : null;
  const awayTeamFaceOffs_Original = data.overwriteEnabled ? data.AwayTeamFaceOffs_Original : null;

  // Sub-components
  const TeamLogo = ({ team, result, position = 'top' }) => {
    const bgClass = result == 'win' ? 'bg-[#53C020]' : result == 'loss' ? 'bg-[#C02020]' : result == 'tie' ? 'bg-[#9B9B9B]' : '';
    const letter = result == 'win' ? 'W' : result == 'loss' ? 'L' : result == 'tie' ? 'T' : null;
    const marginClass = (letter == null) ? 'mt-[10px] mb-[10px]' : '';
    return (
      <div className={`${bgClass} rounded-lg w-[45px] desktop:w-[50px]`}>
        {position == 'top' && letter && <div className="font-roboto-condensed text-center text-white text-[0.7rem] desktop:text-xs py-[1px]">{letter}</div>}
        <div
          className={`${marginClass} w-[45px] desktop:w-[50px] h-[45px] desktop:h-[50px] bg-no-repeat bg-center rounded-lg`}
          style={{ backgroundImage: `url('${getImage(team, 'md')}')`, backgroundSize: '150%' }}>
        </div>
        {position == 'bottom' && letter && <div className="font-roboto-condensed text-center text-white text-[0.7rem] desktop:text-xs py-[1px]">{letter}</div>}
      </div>
    )
  }

  const TeamName = ({ team, align }) => {
    const size = isMobile ? 'small' : 'regular';
    const alignClass = align == 'right' ? 'text-right' : 'text-left';
    const marginClass = align == 'right' ? (size == 'small' ? 'mr-2' : 'mr-4') : (size == 'small' ? 'ml-2' : 'ml-4');
    return (
      <span className={`${marginClass} flex flex-col gap-0 desktop:gap-1`}>
        <span className={`${alignClass} font-roboto-condensed text-[0.9rem] desktop:text-[1.3rem] text-black break-all line-clamp-1 desktop:line-clamp-2`}>{team?.name}</span>
      </span>
    )
  }

  const TeamStats = ({ shots, shots_Original, faceOffs, faceOffs_Original, penaltyMinutes, align }) => {
    const size = isMobile ? 'small' : 'regular';
    const alignClass = align == 'right' ? 'text-right' : 'text-left';
    const marginClass = align == 'right' ? (size == 'small' ? 'mr-2' : 'mr-4') : (size == 'small' ? 'ml-2' : 'ml-4');
    const justifyClass = align == 'right' ? 'justify-end' : 'justify-start';
    if (isMobile) {
      return (
        <div className={`${marginClass} ${alignClass} mt-1 font-light flex flex-col text-[#A1A1A1] text-sm`}>
          <div className={`flex gap-1 ${justifyClass}`}>
            <span className="break-all line-clamp-1">{LANG['EMBED_GAME_SHOTS']}:</span>
            <span>{shots_Original !== null ? `${shots} (${shots_Original})` : `${shots}`}</span>
          </div>
          <div className={`flex gap-1 ${justifyClass}`}>
            <span className="break-all line-clamp-1">{LANG['EMBED_GAME_FACEOFFS_SHORT']}:</span>
            <span>{faceOffs_Original !== null ? `${faceOffs} (${faceOffs_Original})` : `${faceOffs}`}</span>
          </div>
          <div className={`flex gap-1 ${justifyClass}`}>
            <span className="break-all line-clamp-1">{LANG['EMBED_GAME_PENALTYMINUTES_SHORT']}:</span>
            <span>{penaltyMinutes}</span>
          </div>
        </div>
      )
    } else {
      return (
        <div className={`${marginClass} ${alignClass} font-light mt-1 gap-1 text-[#A1A1A1] text-xs`}>
          {LANG['EMBED_GAME_SHOTS']}: {shots_Original !== null ? `${shots} (${shots_Original})` : `${shots}`}
          <span className="inline mx-[3px]">·</span>
          {LANG['EMBED_GAME_FACEOFFS']}: {faceOffs_Original !== null ? `${faceOffs} (${faceOffs_Original})` : `${faceOffs}`}
          <span className="inline mx-[3px]">·</span>
          {LANG['EMBED_GAME_PENALTYMINUTES']}: {penaltyMinutes}
        </div>
      )
    }
  }

  return (
    <EmbedCard className={"desktop:pb-6" + (noBorder ? ' !px-0' : '')}>
      <div className="text-[#A1A1A1] font-light text-xs dekstop:text-sm">
        {/* Mobile: Two lines with justify-between */}
        <div className="flex flex-col gap-1 desktop:hidden">
          <div className="flex justify-between gap-2">
            <span className="truncate">{formatTime(data.startTime)} / {formatDate(data.startDate)}</span>
            <span className="truncate">·</span>
            <span className="truncate">{data.Venue?.name}</span>
            <span className="truncate">·</span>
            <span className="truncate">{data.Phase?.Group?.Season?.Competition?.name}</span>
          </div>
          <div className="flex justify-between gap-2">
            <span className="truncate">{data.Phase?.Group?.name}</span>
            <span className="truncate">·</span>
            <span className="truncate">{data.Phase?.name}</span>
          </div>
        </div>
        
        {/* Desktop: Single line with justify-between */}
        <div className="hidden desktop:flex justify-between gap-2">
          <span className="truncate">{formatTime(data.startTime)} / {formatDate(data.startDate)}</span>
          <span className="truncate">·</span>
          <span className="truncate">{data.Venue?.name}</span>
          <span className="truncate">·</span>
          <span className="truncate">{data.Phase?.Group?.Season?.Competition?.name}</span>
          <span className="truncate">·</span>
          <span className="truncate">{data.Phase?.Group?.name}</span>
          <span className="truncate">·</span>
          <span className="truncate">{data.Phase?.name}</span>
        </div>
      </div>

      <EmbedCardTitleDivier />

      <div className="mb-2 flex justify-between items-end">
        <div className="flex-none w-[45px] desktop:w-[50px]">
          <TeamLogo team={data.HomeTeam} result={getHomeTeamResult(data)} position={isMobile ? "bottom" : "top"} />
        </div>
        <div className="flex-1">
          <TeamName team={data.HomeTeam} align="left" />
          <TeamStats 
            shots={homeTeamShots} 
            shots_Original={homeTeamShots_Original} 
            faceOffs={homeTeamFaceOffs} 
            faceOffs_Original={homeTeamFaceOffs_Original} 
            penaltyMinutes={data.HomeTeamPenaltyMinutes}
            align="left"
          />
        </div>
        <div className="flex-none w-[65px] desktop:w-[100px] self-center">
          <span className="flex no-wrap justify-center items-center gap-2 desktop:font-bold font-score text-[2.5rem] desktop:text-[3rem] text-[#2E2E30]">
            <span>{(data.HomeTeamGoals != 0 || data.AwayTeamGoals != 0 || data.status == 'RUNNING' || data.status == 'FINISHED') ? data.HomeTeamGoals : '–'}</span>
            <span>:</span>
            <span>{(data.HomeTeamGoals != 0 || data.AwayTeamGoals != 0 || data.status == 'RUNNING' || data.status == 'FINISHED') ? data.AwayTeamGoals : '–'}</span>
          </span>
        </div>
        <div className="flex-1">
          <TeamName team={data.AwayTeam} align="right" />
          <TeamStats 
            shots={awayTeamShots} 
            shots_Original={awayTeamShots_Original} 
            faceOffs={awayTeamFaceOffs} 
            faceOffs_Original={awayTeamFaceOffs_Original} 
            penaltyMinutes={data.AwayTeamPenaltyMinutes} 
            align="right"
          />
        </div>
        <div className="flex-none w-[45px] desktop:w-[50px]">
          <TeamLogo team={data.AwayTeam} result={getAwayTeamResult(data)} position={isMobile ? "bottom" : "top"} />
        </div>
      </div>

      {data.overwriteEnabled &&
        <div className="mt-7 flex gap-2 justify-center items-center font-light text-sm text-orange-500">
          <InfoCircleOutlined />
          <span className="font-light text-sm">
            {
              LANG['KONTUMACE_ADJUSTED_MESSAGE']
                .replace('{homeTeamGoals}', data.HomeTeamGoals_Original)
                .replace('{awayTeamGoals}', data.AwayTeamGoals_Original)
            }
          </span>
        </div>
      }
    </EmbedCard>
  );
}

export default GameEmbedInfo; 